import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetOptionGroups } from "../../hooks/optionGroups/useGetOptionGroups";
import { useDeleteOptionGroup } from "../../hooks/optionGroups/useDeleteOptionGroup";

import TableLayout from "../global/Table/TableLayout";
import { OptionGroupsTableColumns } from "./Table/OptionGroupsTableColumns";
import OptionGroupsTableRow from "./Table/OptionGroupsTableRow";
import useTableActions from "../../hooks/common/useTableActions";

const OptionGroupsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const navigate = useNavigate();

    const { deleteOptionGroupMutate, isDeleting } = useDeleteOptionGroup();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/option-groups",
        deleteSingle: deleteOptionGroupMutate,
    });

    //Filter State
    const [statusFilter, setStatusFilter] = useState("");

    const statusOptions = [
        { value: "", label: { ar: "الكل", en: "All" } },
        { value: "1", label: { ar: "نشط", en: "Active" } },
        { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
    ];

    const tableFilters = [
        {
            name: "is_active",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: statusOptions,
        },
    ];

    const params = useMemo(() => {
        return {
            page,
            per_page: perPage,
            search: search || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        };
    }, [page, perPage, search, statusFilter, order]);

    const { optionGroups, meta, isLoading, error } = useGetOptionGroups(params);

    const renderRow = (rowProps) => (
        <OptionGroupsTableRow
            {...rowProps}
            item={rowProps.item}
            isSelected={selectedIds.includes(rowProps.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "مجموعات الخيارات" : "Option Groups";
    const subtitle = isArabic ? "إدارة مجموعات خيارات المنتجات (مثل: اللون، المقاس، السعة)" : "Manage product option groups (e.g., Color, Size, Storage)";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة مجموعة خيارات" : "Add Option Group"}
            onAddClick={() => navigate("/option-groups/create")}
            isLoading={isLoading}
            error={error}
            items={optionGroups}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, optionGroups)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            columns={OptionGroupsTableColumns}
            renderRow={renderRow}
            // Menu Actions
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            isDeletingSingle={isDeleting}
            // Filters
            filters={tableFilters}
            onClearAllFilters={() => setStatusFilter("")}
            emptyLabelAr="لا توجد مجموعات خيارات حالياً"
            emptyLabelEn="No option groups found"
        />
    );
};

export default OptionGroupsTable;
